import { Controller } from "@hotwired/stimulus"

// FD-67: "Convert selection to work item". Captures the text the user has
// selected inside the page body and copies it into the hidden form field that
// posts to pages#convert_selection. The form's <details> menu carries a project
// picker; submitting creates a work item titled with the selection.
//
// FD-910: the capture never ran. `toggle` does not bubble, and the action was bound
// on the <summary> while the event fires on its parent <details>, so the hidden field
// stayed empty and the Create button stayed disabled whatever you had selected. The
// view now binds:
//   pointerdown->selection-to-item#snapshot  on the <summary>
//   toggle->selection-to-item#capture        on the <details>
// The pointerdown snapshot is there because a press on the button can collapse the
// page selection (browser-dependent) before the toggle fires; keyboard opening has no
// press and reads the live selection at toggle time.
//
// Targets:
//   body      — the element whose selected text we read (the rendered page body)
//   input     — hidden field that carries the selected text to the server
//   preview   — small element that echoes the captured selection
//   submit    — the create button (disabled until there's a selection)
export default class extends Controller {
  static targets = ["body", "input", "preview", "submit"]

  // Runs before the press's default action, so the selection is still intact.
  snapshot() {
    this.snapshotText = this.readSelection()
  }

  capture(event) {
    const snapshot = this.snapshotText
    this.snapshotText = ""
    if (event?.currentTarget?.open === false) return

    const text = this.readSelection() || snapshot || ""
    this.inputTarget.value = text
    if (this.hasPreviewTarget) {
      this.previewTarget.textContent = text ? `“${text.slice(0, 120)}”` : "Select text in the page first, then reopen this menu."
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = text.length === 0
  }

  // The selected text, whitespace collapsed (a multi-paragraph selection would
  // otherwise carry newlines into the title), or "" unless it lies within the body.
  readSelection() {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return ""

    const text = sel.toString().replace(/\s+/g, " ").trim()
    if (!text) return ""
    if (this.hasBodyTarget && !this.bodyTarget.contains(sel.getRangeAt(0).commonAncestorContainer)) return ""
    return text
  }
}
